import { Button, ScrollArea } from 'shared-ui';

import type { PoiHistoryEvent } from '@/api/poiHistoryApi';
import { usePoiHistory } from '@/hooks/usePoiHistory';
import { PoiHistoryTimeline } from './PoiHistoryTimeline';

function EventCardSkeleton() {
    return (
        <div className="bg-card space-y-4 rounded-md border p-6" aria-hidden="true">
            <span className="bg-muted block h-6 w-40 animate-pulse rounded" />
            <span className="bg-muted block h-4 w-24 animate-pulse rounded" />
            <span className="bg-muted block h-4 w-28 animate-pulse rounded" />
        </div>
    );
}

interface PoiHistoryTabProps {
    poiId: string;
    /**
     * The client the POI belongs to — the selected client in Find & Fix, the record's own
     * client in the Network Explorer, where records of several clients share one map.
     */
    clientId: string | null;
    /** False until the user first opens the History tab for this POI — nothing fetches before then. */
    enabled: boolean;
    onViewDetails: (event: PoiHistoryEvent) => void;
}

/**
 * The History tab body: fetches the unified timeline for the selected POI (lazily, on
 * first tab activation) and renders the loading / error / empty / timeline states.
 */
export function PoiHistoryTab({ poiId, clientId, enabled, onViewDetails }: PoiHistoryTabProps) {
    const { history, isLoading, isError, retry } = usePoiHistory(poiId, clientId, enabled);

    return (
        <ScrollArea className="min-h-0 flex-1">
            {isLoading && (
                <div className="space-y-4 px-6 py-4" role="status" aria-label="Loading history">
                    <EventCardSkeleton />
                    <EventCardSkeleton />
                    <EventCardSkeleton />
                </div>
            )}
            {isError && (
                <div className="space-y-3 px-6 py-4">
                    <p className="text-destructive text-sm">Unable to load history — please try again.</p>
                    <Button
                        type="button"
                        variant="ghost"
                        className="panel-close-btn text-primary h-10 cursor-pointer"
                        onClick={retry}
                    >
                        Try Again
                    </Button>
                </div>
            )}
            {history && history.events.length === 0 && (
                <p className="text-muted-foreground px-6 py-4 text-sm">
                    {/* Entity-neutral: this tab serves the POI panel and the leak panel —
                        the timeline is the whole family's, so neither entity may claim it. */}
                    No history recorded.
                </p>
            )}
            {history && history.events.length > 0 && (
                <PoiHistoryTimeline events={history.events} onViewDetails={onViewDetails} />
            )}
        </ScrollArea>
    );
}
